/**
 * Screen component — shared types and Zod prop schema.
 *
 * A full-screen layout wrapper that arranges content within safe areas,
 * an optional scroll region, and an optional pinned footer. Screen is
 * the outermost container for any spec-rendered screen.
 *
 * Accessibility: Screen announces its accessibilityTitle to screen readers on mount.
 * It does not add an interactive role — children retain their own semantics.
 *
 * screenId: Identifier for analytics (screen_view event) and deeplinks.
 * Production screens MUST use a registered ScreenId from ADR-035
 * (packages/shared/src/screens/registry.ts). Preview/demo screens
 * use a `demo-` prefix by convention. Validation happens at the
 * deeplink/analytics boundary, not the rendering boundary — the schema
 * accepts any string so LLM-generated screens can render before registration.
 */

import { z } from 'zod';
import { spacing, servedSpacingKeys, SCREEN_GUTTER_KEY } from '../../elements/spacing';

export const ScreenFooterBottomPadding = z.enum(['none', ...servedSpacingKeys]);
export type ScreenFooterBottomPadding = z.infer<typeof ScreenFooterBottomPadding>;
export const ScreenHorizontalPadding = z.enum(servedSpacingKeys);
export type ScreenHorizontalPadding = z.infer<typeof ScreenHorizontalPadding>;
export const ScreenContentPaddingTop = z.enum(['none', ...servedSpacingKeys]);
export type ScreenContentPaddingTop = z.infer<typeof ScreenContentPaddingTop>;

export const screenPropsSchema = z.object({
  screenId: z.string(),
  accessibilityTitle: z.string(),
  scrollable: z.boolean().default(true),
  hasFooter: z.boolean().default(false),
  /**
   * Horizontal inset for scroll/fixed content. Defaults to the 16px screen-edge
   * margin, which suits card-based content (a card carries its own inner
   * padding). A screen whose top-level content is text or UI that does not live
   * in a card passes `'lg'` explicitly — see ADR-091.
   */
  // The default screen gutter, owned by elements/spacing so the bleeding
  // components that cancel it (ADR-020 Rule 2 carve-out) read the same value.
  contentPaddingHorizontal: ScreenHorizontalPadding.default(SCREEN_GUTTER_KEY),
  /**
   * Horizontal inset for the pinned footer. Defaults to the same margin as the
   * content, so the two stay aligned; a screen that widens its content to `'lg'`
   * widens the footer to match (ADR-091).
   */
  footerPaddingHorizontal: ScreenHorizontalPadding.default(SCREEN_GUTTER_KEY),
  /**
   * Reserve space at the bottom of the scroll content for an app-shell bottom
   * tab bar (the `sizing.bottomNav` height), so the last row can scroll clear of
   * it. Opt-in; screens not mounted under the tab bar (modals, flows) leave it
   * false. Only affects scrollable screens.
   */
  tabBarInset: z.boolean().default(false),
  edges: z
    .array(z.enum(['top', 'bottom', 'left', 'right']))
    .default(['top', 'bottom', 'left', 'right']),
  /**
   * Whether the pinned footer renders a top divider line separating it from the
   * scroll content. Default true preserves the standard footer treatment; set
   * false on screens whose footer should sit flush against the content (e.g. a
   * single-button welcome screen with no list above it).
   */
  footerDivider: z.boolean().default(true),
  /**
   * Whether a fade gradient (transparent → screen background) is drawn at the
   * bottom of the scroll region, so content scrolling under the pinned footer
   * fades out instead of being hard-cut at the footer's top edge. Default true;
   * native scrolling screens also use it at a footerless edge. Browser rendering
   * retains the footer-only treatment.
   */
  footerFade: z.boolean().default(true),
  /**
   * Whether the pinned footer lifts above the on-screen keyboard. Default true
   * (chat/form footers stay visible while typing). Set false on screens whose
   * footer CTAs should stay pinned at the bottom behind the keyboard — the
   * member dismisses the keyboard (return key) to reveal them.
   */
  footerLiftsWithKeyboard: z.boolean().default(true),
  /**
   * Whether the scroll region keeps the focused input visible above the on-screen
   * keyboard (auto-scrolls it into view). Opt-in — enable on screens whose inputs
   * sit low enough to be covered by the keyboard. Only affects scrollable screens.
   */
  keyboardAwareContent: z.boolean().default(false),
  /** Tokenized top inset for the content viewport. Defaults to the standard
   * screen spacing; dense, illustration-led screens may opt out explicitly. */
  contentPaddingTop: ScreenContentPaddingTop.default('m'),
  footerBottomPadding: ScreenFooterBottomPadding.default('m'),
});

export type ScreenProps = z.infer<typeof screenPropsSchema>;

/**
 * Height of the independent footerless fade and the legacy/web content fade.
 * Native overlay footers instead wash across their measured height and safe area.
 */
export const SCREEN_CONTENT_FADE_HEIGHT = spacing.xl * 2;

/**
 * Height of the fade beneath a top header. Shorter than the footer fade so it
 * softens the top edge without masking the content that sits just below the
 * header at rest.
 */
export const SCREEN_HEADER_FADE_HEIGHT = spacing.lg;

/** Gap kept between a focused input and the top of the keyboard when auto-scrolled. */
export const SCREEN_KEYBOARD_BOTTOM_OFFSET = spacing.lg;

export function screenFooterBottomPaddingValue(key: ScreenFooterBottomPadding): number {
  if (key === 'none') return 0;
  return spacing[key];
}

export function screenContentPaddingTopValue(key: ScreenContentPaddingTop): number {
  if (key === 'none') return 0;
  return spacing[key];
}

/**
 * Footer keyboard lift: the keyboard height (screen-bottom-anchored, both
 * platforms — react-native-keyboard-controller's contract) less the safe-area
 * bottom inset the SafeAreaView has already reserved. Discounted exactly once;
 * clamped so a hidden/floating keyboard never produces a negative lift.
 * Runs inside a reanimated worklet on native, hence the directive.
 */
export function screenFooterLift(keyboardHeight: number, reservedBottomInset: number): number {
  'worklet';
  return Math.max(0, keyboardHeight - reservedBottomInset);
}
